import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './Financeiro.css';

/**
 * Página Financeiro - Consolidado e por Rodada
 * Para jogadores: mostra apenas consolidado geral e seu próprio saldo
 * Para admin: mostra tudo
 */

const Financeiro = () => {
  const { user, isAdmin } = useAuth();
  // Jogadores só veem por rodada, admin pode ver consolidado também
  const [viewMode, setViewMode] = useState(isAdmin ? 'consolidado' : 'rodada');
  const [resumoConsolidado, setResumoConsolidado] = useState(null);
  const [resumoRodada, setResumoRodada] = useState(null);
  const [rodadas, setRodadas] = useState([]);
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (viewMode === 'rodada' && rodadaSelecionada) {
      carregarResumoRodada();
    }
  }, [viewMode, rodadaSelecionada]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [resumoRes, rodadasRes] = await Promise.all([
        apiClient.get('/financeiro/resumo'),
        apiClient.get('/rodadas'),
      ]);

      setResumoConsolidado(resumoRes.data);
      // Permitir visualizar rodadas abertas também (tempo real)
      const todasRodadas = rodadasRes.data;
      setRodadas(todasRodadas);

      // Priorizar rodada aberta, senão primeira fechada
      const rodadaAberta = todasRodadas.find(r => r.status === 'aberta');
      if (rodadaAberta && !rodadaSelecionada) {
        setRodadaSelecionada(rodadaAberta.id);
      } else if (todasRodadas.filter(r => r.status === 'fechada').length > 0 && !rodadaSelecionada) {
        setRodadaSelecionada(todasRodadas.filter(r => r.status === 'fechada')[0].id);
      }

      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const carregarResumoRodada = async () => {
    if (!rodadaSelecionada) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/financeiro/rodada/${rodadaSelecionada}`);
      console.log('📊 Resumo rodada carregado:', response.data);
      console.log('   Participações:', response.data.saldo_por_jogador?.map(p => ({
        jogador: p.jogador?.nome,
        participacao_id: p.participacao_id,
        saldo_final: p.saldo_final,
        saldo_final_pago: p.saldo_final_pago,
      })));
      setResumoRodada(response.data);
      setError('');
    } catch (err) {
      console.error('Erro ao carregar resumo da rodada:', err);
      setError(err.response?.data?.error || 'Erro ao carregar resumo da rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarSaldoComoPago = async (participacaoId) => {
    if (!window.confirm('Deseja marcar o saldo financeiro deste jogador como pago?')) {
      return;
    }

    try {
      setSalvando(true);
      await apiClient.put(`/financeiro/rodada/${rodadaSelecionada}/marcar-pago`, {
        participacao_id: participacaoId,
      });

      alert('Saldo financeiro marcado como pago com sucesso!');
      carregarResumoRodada();
    } catch (err) {
      console.error('Erro ao marcar como pago:', err);
      setError(err.response?.data?.error || 'Erro ao marcar como pago');
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelarLancamento = async (jogadorId, jogadorNome, tipo, quantidade = 1) => {
    let mensagem = '';
    switch (tipo) {
      case 'rebuy_simples':
        mensagem = `Deseja cancelar ${quantidade} rebuy(s) simples de ${jogadorNome}?`;
        break;
      case 'rebuy_duplo':
        mensagem = `Deseja cancelar ${quantidade} rebuy(s) duplo(s) de ${jogadorNome}?`;
        break;
      case 'addon':
        mensagem = `Deseja cancelar o addon de ${jogadorNome}?`;
        break;
      case 'janta':
        mensagem = `Deseja cancelar a participação na janta de ${jogadorNome}?`;
        break;
      default:
        mensagem = `Deseja cancelar este lançamento de ${jogadorNome}?`;
    }

    if (!window.confirm(mensagem)) {
      return;
    }

    try {
      setCancelando(true);
      await apiClient.delete(`/rodadas/${rodadaSelecionada}/lancamento`, {
        data: { tipo, jogador_id: jogadorId, quantidade },
      });

      alert('Lançamento cancelado com sucesso!');
      carregarResumoRodada();
    } catch (err) {
      console.error('Erro ao cancelar lançamento:', err);
      setError(err.response?.data?.error || 'Erro ao cancelar lançamento');
      alert(err.response?.data?.error || 'Erro ao cancelar lançamento');
    } finally {
      setCancelando(false);
    }
  };

  const handleEstornarEntradaAntecipada = async (jogadorId, jogadorNome) => {
    if (!window.confirm(`Deseja estornar a entrada antecipada de ${jogadorNome}?`)) {
      return;
    }

    try {
      setCancelando(true);
      await apiClient.delete(`/rodadas/${rodadaSelecionada}/entrada-antecipada`, {
        data: { jogador_id: jogadorId },
      });

      alert('Entrada antecipada estornada com sucesso!');
      carregarResumoRodada();
    } catch (err) {
      console.error('Erro ao estornar entrada antecipada:', err);
      setError(err.response?.data?.error || 'Erro ao estornar entrada antecipada');
      alert(err.response?.data?.error || 'Erro ao estornar entrada antecipada');
    } finally {
      setCancelando(false);
    }
  };

  if (loading && !resumoConsolidado && !resumoRodada) {
    return <div className="container">Carregando...</div>;
  }

  const renderConsolidado = () => {
    if (!resumoConsolidado) {
      return (
        <div className="no-data">
          <p>Nenhum dado financeiro consolidado disponível ainda.</p>
        </div>
      );
    }

    return (
      <>
        {/* Totais Consolidados - Visível para todos */}
        <div className="financeiro-grid">
          <div className="financeiro-card">
            <h3>Total Arrecadado</h3>
            <p className="valor positivo">R$ {parseFloat(resumoConsolidado.total_arrecadado || 0).toFixed(2)}</p>
          </div>

          <div className="financeiro-card">
            <h3>Total Prêmios Pagos</h3>
            <p className="valor negativo">R$ {parseFloat(resumoConsolidado.total_premios || 0).toFixed(2)}</p>
          </div>

          <div className="financeiro-card">
            <h3>Caixinha NerdzPoker</h3>
            <p className="valor caixinha">R$ {parseFloat(resumoConsolidado.total_caixinha || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Detalhamento Individual - Admin vê todos, Jogador vê apenas o seu */}
        <div className="saldos-section">
          <h2>{isAdmin ? 'Saldos Consolidados por Jogador' : 'Meu Saldo Consolidado'}</h2>
          <div className="saldos-container">
            <table className="saldos-table">
              <thead>
                <tr>
                  <th>Jogador</th>
                  <th>Saldo Final</th>
                  <th>Total a Receber</th>
                  <th>Total Devido</th>
                </tr>
              </thead>
              <tbody>
                {isAdmin ? (
                  // Admin vê todos os jogadores
                  resumoConsolidado.saldo_por_jogador.map((item, index) => (
                    <tr key={item.jogador.id}>
                      <td>{item.jogador.nome}</td>
                      <td className={item.saldo_final >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                        R$ {parseFloat(item.saldo_final).toFixed(2)}
                      </td>
                      <td className="saldo-positivo">
                        {item.total_receber > 0 ? `R$ ${parseFloat(item.total_receber).toFixed(2)}` : '-'}
                      </td>
                      <td className="saldo-negativo">
                        {item.total_devido > 0 ? `R$ ${parseFloat(item.total_devido).toFixed(2)}` : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  // Jogador vê apenas seu próprio saldo
                  (() => {
                    const meuSaldo = resumoConsolidado.saldo_por_jogador.find(
                      item => item.jogador.id === user?.id
                    );
                    if (!meuSaldo) {
                      return (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                            Nenhum saldo encontrado para você ainda.
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={meuSaldo.jogador.id}>
                        <td>{meuSaldo.jogador.nome}</td>
                        <td className={meuSaldo.saldo_final >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                          R$ {parseFloat(meuSaldo.saldo_final).toFixed(2)}
                        </td>
                        <td className="saldo-positivo">
                          {meuSaldo.total_receber > 0 ? `R$ ${parseFloat(meuSaldo.total_receber).toFixed(2)}` : '-'}
                        </td>
                        <td className="saldo-negativo">
                          {meuSaldo.total_devido > 0 ? `R$ ${parseFloat(meuSaldo.total_devido).toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    );
                  })()
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderPorRodada = () => {
    if (!resumoRodada || rodadas.length === 0) {
      return (
        <div className="no-data">
          <p>Nenhuma rodada disponível ainda.</p>
        </div>
      );
    }

    return (
      <>
        <div className="form-group rodada-selector">
          <label>Selecione a Rodada:</label>
          <select
            value={rodadaSelecionada}
            onChange={(e) => setRodadaSelecionada(e.target.value)}
            className="select-rodada"
          >
            {rodadas.map(rodada => (
              <option key={rodada.id} value={rodada.id}>
                {new Date(rodada.data).toLocaleDateString('pt-BR')} - {rodada.status === 'fechada' ? 'Fechada' : 'Aberta'}
              </option>
            ))}
          </select>
        </div>

        {resumoRodada && (
          <>
            {/* Badge de Status da Rodada */}
            <div style={{
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              background: resumoRodada.rodada?.status === 'aberta' ? '#fff3cd' : '#d4edda',
              border: `2px solid ${resumoRodada.rodada?.status === 'aberta' ? '#ffc107' : '#28a745'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {resumoRodada.rodada?.status === 'aberta' ? (
                <>
                  <span style={{ fontSize: '1.5em' }}>⚠️</span>
                  <div>
                    <strong style={{ fontSize: '1.1em' }}>Rodada em Andamento - Valores Parciais</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                      Os valores exibidos são calculados em tempo real e podem mudar conforme novos lançamentos são feitos.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '1.5em' }}>✅</span>
                  <div>
                    <strong style={{ fontSize: '1.1em' }}>Rodada Encerrada</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                      Os valores estão finalizados e congelados.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="financeiro-grid">
              <div className="financeiro-card">
                <h3>Total Arrecadado</h3>
                <p className="valor positivo">R$ {parseFloat(resumoRodada.total_arrecadado).toFixed(2)}</p>
                <small>Rodada: {new Date(resumoRodada.rodada.data).toLocaleDateString('pt-BR')}</small>
              </div>

              <div className="financeiro-card">
                <h3>Total Prêmios Pagos</h3>
                <p className="valor negativo">R$ {parseFloat(resumoRodada.total_premios).toFixed(2)}</p>
              </div>

              <div className="financeiro-card">
                <h3>Caixinha da Rodada</h3>
                <p className="valor caixinha">R$ {parseFloat(resumoRodada.total_caixinha).toFixed(2)}</p>
                {resumoRodada.itens_direto_caixinha > 0 && (
                  <small>Itens diretos: R$ {parseFloat(resumoRodada.itens_direto_caixinha).toFixed(2)}</small>
                )}
                {resumoRodada.caixinha_percentual > 0 && (
                  <small>Percentual: R$ {parseFloat(resumoRodada.caixinha_percentual).toFixed(2)}</small>
                )}
              </div>

              {resumoRodada.total_gastos > 0 && (
                <div className="financeiro-card">
                  <h3>Gastos NerdzPoker</h3>
                  <p className="valor negativo">R$ {parseFloat(resumoRodada.total_gastos).toFixed(2)}</p>
                  {resumoRodada.gastos && resumoRodada.gastos.length > 0 && (
                    <details style={{ marginTop: '10px', fontSize: '0.85em' }}>
                      <summary style={{ cursor: 'pointer', color: '#666' }}>Ver detalhes dos gastos</summary>
                      <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                        {resumoRodada.gastos.map((gasto, idx) => (
                          <li key={idx}>
                            {gasto.descricao}: R$ {parseFloat(gasto.valor).toFixed(2)}
                            {gasto.data_gasto && (
                              <span style={{ color: '#999', marginLeft: '10px' }}>
                                ({new Date(gasto.data_gasto).toLocaleDateString('pt-BR')})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {isAdmin && resumoRodada.janta && resumoRodada.janta.custo_total > 0 && (
                <div className="financeiro-card">
                  <h3>Janta</h3>
                  <p className="valor janta">R$ {parseFloat(resumoRodada.janta.custo_total).toFixed(2)}</p>
                  <small>{resumoRodada.janta.participantes} participante(s)</small>
                </div>
              )}

              {!isAdmin && resumoRodada.janta && resumoRodada.janta.custo_total > 0 && (
                <div className="financeiro-card">
                  <h3>Janta</h3>
                  <p className="valor janta">R$ {parseFloat(resumoRodada.janta.custo_total).toFixed(2)}</p>
                  <small>
                    {resumoRodada.janta.participantes} participante(s) - 
                    {(() => {
                      const meuSaldo = resumoRodada.saldo_por_jogador.find(
                        item => item.jogador.id === user?.id
                      );
                      if (meuSaldo && meuSaldo.participou_janta) {
                        return ` Você deve: R$ ${parseFloat(meuSaldo.janta_paga).toFixed(2)}`;
                      }
                      return ' Você não participou';
                    })()}
                  </small>
                </div>
              )}

              <div className="financeiro-card">
                <h3>Participantes</h3>
                <p className="valor">{resumoRodada.total_participantes}</p>
              </div>
            </div>

            {/* Detalhamento Individual - Admin vê todos, Jogador vê tabela detalhada */}
            <div className="saldos-section">
              <h2>{isAdmin ? 'Detalhamento por Jogador' : 'Meu Financeiro por Rodada'}</h2>
              <div className="saldos-container">
                {isAdmin ? (
                  // Admin vê tabela resumida
                  <table className="saldos-table">
                    <thead>
                      <tr>
                        <th>Jogador</th>
                        <th>Entrada Antecipada</th>
                        <th>Entrada</th>
                        <th>Buy-ins</th>
                        <th>Add-on</th>
                        <th>Saldo Devedor Poker</th>
                        <th>Prêmio Recebido</th>
                        <th>Saldo Final Poker</th>
                        <th>Rateio Janta</th>
                        <th>Jackpot</th>
                        <th>Saldo Total</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoRodada.saldo_por_jogador.map((item, index) => {
                        // Usar novos campos do backend
                        const saldoDevedorPoker = item.saldo_devedor_poker || item.saldo_devedor || 0;
                        const saldoFinalPoker = item.saldo_final_poker || item.saldo_final || 0;
                        const rateioJanta = item.rateio_janta || 0;
                        const jackpot = item.jackpot || 0;
                        const saldoTotal = item.saldo_total || 0;
                        
                        return (
                          <tr key={item.jogador.id}>
                            <td>{item.jogador.nome}</td>
                            <td style={{ textAlign: 'center' }}>
                              {item.entrada_antecipada ? (
                                <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✓</span>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td>R$ {parseFloat(item.entrada).toFixed(2)}</td>
                            <td>R$ {parseFloat(item.buyins).toFixed(2)}</td>
                            <td>{item.addon ? `R$ ${parseFloat(item.addon_valor || 0).toFixed(2)}` : 'R$ 0,00'}</td>
                            <td className={saldoDevedorPoker > 0 ? 'saldo-negativo' : ''}>
                              R$ {parseFloat(saldoDevedorPoker).toFixed(2)}
                            </td>
                            <td>R$ {parseFloat(item.premio_recebido).toFixed(2)}</td>
                            <td className={saldoFinalPoker >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                              R$ {parseFloat(saldoFinalPoker).toFixed(2)}
                            </td>
                            <td className={rateioJanta < 0 ? 'saldo-negativo' : ''}>
                              {rateioJanta !== 0 ? `R$ ${parseFloat(rateioJanta).toFixed(2)}` : 'R$ 0,00'}
                            </td>
                            <td className={jackpot < 0 ? 'saldo-negativo' : jackpot > 0 ? 'saldo-positivo' : ''}>
                              {jackpot !== 0 ? `R$ ${parseFloat(jackpot).toFixed(2)}` : 'R$ 0,00'}
                            </td>
                            <td className={saldoTotal >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                              <strong>R$ {parseFloat(saldoTotal).toFixed(2)}</strong>
                            </td>
                            <td>
                              {item.saldo_final_pago ? (
                                <span className="status-pago">✅ Pago</span>
                              ) : (
                                <span className="status-pendente">⏳ Pendente</span>
                              )}
                            </td>
                            <td>
                              {/* Ações apenas para admin e rodada aberta */}
                              {isAdmin && resumoRodada.rodada?.status === 'aberta' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
                                  {/* Estornar entrada antecipada */}
                                  {item.entrada_antecipada && (
                                    <button
                                      onClick={() => handleEstornarEntradaAntecipada(item.jogador.id, item.jogador.nome)}
                                      disabled={cancelando}
                                      className="btn btn-danger"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Estornar entrada antecipada"
                                    >
                                      🗑️ Entrada
                                    </button>
                                  )}
                                  {/* Cancelar rebuy simples */}
                                  {(item.rebuys_simples_qtd > 0 || (item.buyin_simples && item.buyin_simples > 1)) && (
                                    <button
                                      onClick={() => handleCancelarLancamento(item.jogador.id, item.jogador.nome, 'rebuy_simples', 1)}
                                      disabled={cancelando}
                                      className="btn btn-warning"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Cancelar 1 rebuy simples"
                                    >
                                      🗑️ Rebuy Simples
                                    </button>
                                  )}
                                  {/* Cancelar rebuy duplo */}
                                  {(item.rebuys_duplos_qtd > 0 || (item.buyin_duplo && item.buyin_duplo > 0)) && (
                                    <button
                                      onClick={() => handleCancelarLancamento(item.jogador.id, item.jogador.nome, 'rebuy_duplo', 1)}
                                      disabled={cancelando}
                                      className="btn btn-warning"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Cancelar 1 rebuy duplo"
                                    >
                                      🗑️ Rebuy Duplo
                                    </button>
                                  )}
                                  {/* Cancelar addon */}
                                  {item.addon && (
                                    <button
                                      onClick={() => handleCancelarLancamento(item.jogador.id, item.jogador.nome, 'addon')}
                                      disabled={cancelando}
                                      className="btn btn-warning"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Cancelar addon"
                                    >
                                      🗑️ Addon
                                    </button>
                                  )}
                                  {/* Cancelar janta */}
                                  {item.participou_janta && (
                                    <button
                                      onClick={() => handleCancelarLancamento(item.jogador.id, item.jogador.nome, 'janta')}
                                      disabled={cancelando}
                                      className="btn btn-warning"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Cancelar participação na janta"
                                    >
                                      🗑️ Janta
                                    </button>
                                  )}
                                  {/* Marcar como pago (sempre disponível) */}
                                  {!item.saldo_final_pago && (
                                    <button
                                      onClick={() => handleMarcarSaldoComoPago(item.participacao_id)}
                                      disabled={salvando}
                                      className="btn btn-success"
                                      style={{ padding: '3px 8px', fontSize: '11px' }}
                                      title="Marcar saldo como pago"
                                    >
                                      ✓ Marcar Pago
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* Para rodada fechada ou não-admin, apenas mostrar botão de marcar como pago */}
                              {isAdmin && resumoRodada.rodada?.status === 'fechada' && !item.saldo_final_pago && (
                                <button
                                  onClick={() => handleMarcarSaldoComoPago(item.participacao_id)}
                                  disabled={salvando}
                                  className="btn btn-success"
                                  style={{ padding: '5px 10px', fontSize: '12px' }}
                                  title="Marcar saldo como pago"
                                >
                                  ✓ Marcar Pago
                                </button>
                              )}
                              {!isAdmin && (
                                <span style={{ color: '#999', fontSize: '0.9em' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  // Jogador vê tabela detalhada
                  (() => {
                    const meuSaldo = resumoRodada.saldo_por_jogador.find(
                      item => item.jogador.id === user?.id
                    );
                    if (!meuSaldo) {
                      return (
                        <div className="no-data">
                          <p>Você não participou desta rodada.</p>
                        </div>
                      );
                    }
                    return (
                      <>
                        {meuSaldo.posicao_final && (
                          <div style={{ 
                            marginBottom: '20px', 
                            padding: '15px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '8px',
                            textAlign: 'center',
                            color: 'white'
                          }}>
                            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>
                              Sua Posição na Rodada
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
                              {meuSaldo.posicao_final}º Lugar
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                              Prêmio baseado na sua posição final
                            </div>
                          </div>
                        )}
                        <table className="saldos-table detalhado">
                          <tbody>
                            <tr>
                              <td><strong>Entrada</strong></td>
                              <td className="valor-item">R$ {parseFloat(meuSaldo.entrada || 40).toFixed(2)}</td>
                            </tr>
                          <tr>
                            <td><strong>Buy-in Inicial</strong></td>
                            <td className="valor-item">R$ {parseFloat(meuSaldo.buyin_inicial_valor || 25).toFixed(2)}</td>
                          </tr>
                          {meuSaldo.rebuys_simples_qtd > 0 && (
                            <tr>
                              <td><strong>Rebuys Simples</strong></td>
                              <td className="valor-item">
                                {meuSaldo.rebuys_simples_qtd} x R$ 25,00 = R$ {parseFloat(meuSaldo.rebuys_simples_valor).toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {meuSaldo.rebuys_duplos_qtd > 0 && (
                            <tr>
                              <td><strong>Rebuys Duplos</strong></td>
                              <td className="valor-item">
                                {meuSaldo.rebuys_duplos_qtd} x R$ {(parseFloat(meuSaldo.rebuys_duplos_valor) / (meuSaldo.rebuys_duplos_qtd || 1)).toFixed(2)} = R$ {parseFloat(meuSaldo.rebuys_duplos_valor).toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {meuSaldo.addon && (
                            <tr>
                              <td><strong>Add-On</strong></td>
                              <td className="valor-item">R$ {parseFloat(meuSaldo.addon_valor).toFixed(2)}</td>
                            </tr>
                          )}
                          <tr className="separador">
                            <td><strong>Saldo Devedor Poker</strong></td>
                            <td className="valor-item negativo">
                              R$ {parseFloat(meuSaldo.saldo_devedor_poker || meuSaldo.saldo_devedor || 0).toFixed(2)}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Prêmio a Receber</strong></td>
                            <td className="valor-item positivo">
                              R$ {parseFloat(meuSaldo.premio_receber).toFixed(2)}
                              {meuSaldo.posicao_final && (
                                <span style={{ 
                                  fontSize: '12px', 
                                  color: '#666', 
                                  display: 'block',
                                  marginTop: '4px',
                                  fontStyle: 'italic'
                                }}>
                                  (Baseado na {meuSaldo.posicao_final}ª posição)
                                </span>
                              )}
                            </td>
                          </tr>
                          <tr className="separador">
                            <td><strong>Saldo Final Poker</strong></td>
                            <td className={`valor-item ${(meuSaldo.saldo_final_poker || meuSaldo.saldo_final || 0) >= 0 ? 'positivo' : 'negativo'}`}>
                              R$ {parseFloat(meuSaldo.saldo_final_poker || meuSaldo.saldo_final || 0).toFixed(2)}
                            </td>
                          </tr>
                          {meuSaldo.rateio_janta !== undefined && meuSaldo.rateio_janta !== 0 && (
                            <tr>
                              <td><strong>Rateio Janta</strong></td>
                              <td className={`valor-item ${meuSaldo.rateio_janta < 0 ? 'negativo' : ''}`}>
                                R$ {parseFloat(meuSaldo.rateio_janta).toFixed(2)}
                              </td>
                            </tr>
                          )}
                          {meuSaldo.jackpot !== undefined && meuSaldo.jackpot !== 0 && (
                            <tr>
                              <td><strong>Jackpot</strong></td>
                              <td className={`valor-item ${meuSaldo.jackpot < 0 ? 'negativo' : meuSaldo.jackpot > 0 ? 'positivo' : ''}`}>
                                {meuSaldo.jackpot > 0 ? '+' : ''}R$ {parseFloat(meuSaldo.jackpot).toFixed(2)}
                                {meuSaldo.jackpot > 0 && (
                                  <span style={{ 
                                    fontSize: '12px', 
                                    color: '#666', 
                                    display: 'block',
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                  }}>
                                    (Ganhador do Jackpot)
                                  </span>
                                )}
                              </td>
                            </tr>
                          )}
                          <tr className="total-row">
                            <td><strong>Saldo Total</strong></td>
                            <td className={`valor-item ${(meuSaldo.saldo_total || 0) >= 0 ? 'positivo' : 'negativo'}`}>
                              <strong>R$ {parseFloat(meuSaldo.saldo_total || 0).toFixed(2)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      </>
                    );
                  })()
                )}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>💰 Financeiro</h1>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Tabs apenas para admin - jogadores só veem por rodada */}
      {isAdmin && (
        <div className="financeiro-tabs">
          <button
            className={`tab-button ${viewMode === 'consolidado' ? 'active' : ''}`}
            onClick={() => setViewMode('consolidado')}
          >
            📊 Consolidado Geral
          </button>
          <button
            className={`tab-button ${viewMode === 'rodada' ? 'active' : ''}`}
            onClick={() => setViewMode('rodada')}
          >
            🎲 Por Rodada
          </button>
        </div>
      )}

      {viewMode === 'consolidado' ? renderConsolidado() : renderPorRodada()}
    </div>
  );
};

export default Financeiro;
